"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format, parseISO, isValid, subDays } from "date-fns";
import api from "../api/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  AlertCircle,
  RefreshCw,
  Users,
  Calendar,
  TrendingUp,
  UserCheck,
  Shield,
} from "lucide-react";
import { Badge } from "./ui/badge";

export default function UserAnalyticsChart() {
  const [range, setRange] = useState("1m");
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Mock data for debugging when API fails
  const mockData = {
    summary: {
      total: 1524,
      week: 45,
      month: 189,
      threeMonths: 523,
      year: 1247,
    },
    daily: Array.from({ length: 30 }, (_, i) => ({
      date: format(subDays(new Date(), 29 - i), "yyyy-MM-dd"),
      count: Math.floor(Math.random() * 20) + 5,
    })),
    recentUsers: Array.from({ length: 10 }, (_, i) => ({
      id: i + 1,
      email: `user${i + 1}@example.com`,
      role: i % 3 === 0 ? "admin" : i % 3 === 1 ? "moderator" : "user",
      isVerified: i % 4 !== 0,
      createdAt: format(subDays(new Date(), i), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    })),
    page: 1,
    totalPages: 1,
  };

  const normalizeDate = (raw) => {
    try {
      const parsed = parseISO(raw);
      if (!isValid(parsed)) return null;
      return format(parsed, "yyyy-MM-dd");
    } catch (err) {
      console.error("Date parsing error:", err);
      return null;
    }
  };

  const fetchData = useCallback(
    async (r = range, p = 1) => {
      setLoading(true);
      setError(null);
      setIsRefreshing(true);

      try {
        const res = await api.get(
          `/admin/user-analytics?range=${r}&page=${p}&limit=10`,
          {
            timeout: 10000, // 10 second timeout
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "X-User-Name": localStorage.getItem("userName") || "admin",
            },
          }
        );

        if (res.data) {
          setSummary(res.data.summary || mockData.summary);
          setDaily(res.data.daily || mockData.daily);
          setRecent(res.data.recentUsers || mockData.recentUsers);
          setPage(res.data.page || 1);
          setTotalPages(res.data.totalPages || 1);
        }
      } catch (err) {
        console.error("Analytics fetch error details:", {
          message: err.message,
          code: err.code,
          status: err.response?.status,
          data: err.response?.data,
          config: err.config,
        });

        // Show error to user
        setError({
          message:
            err.response?.data?.message ||
            err.message ||
            "Failed to load analytics data",
          status: err.response?.status || 500,
        });

        // Fallback to mock data for debugging
        setSummary(mockData.summary);
        setDaily(mockData.daily);
        setRecent(mockData.recentUsers);
        setPage(1);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [range]
  );

  useEffect(() => {
    fetchData(range, 1);
  }, [range, fetchData]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    fetchData(range, page);
  };

  const handleRefresh = () => {
    fetchData(range, page);
  };

  const rangeButtons = [
    { label: "Last 7 days", value: "7d", icon: Calendar },
    { label: "Last month", value: "1m", icon: TrendingUp },
    { label: "Last 3 months", value: "3m", icon: Users },
    { label: "Last year", value: "1y", icon: UserCheck },
    { label: "All time", value: "all", icon: Shield },
  ];

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(range, newPage);
    }
  };

  const getRangeLabel = (value) => {
    const btn = rangeButtons.find((b) => b.value === value);
    return btn ? btn.label : value;
  };

  return (
    <Card className="w-full border shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                User Registration Analytics
              </CardTitle>
              <CardDescription className="mt-1 text-sm">
                Monitoring user growth for {getRangeLabel(range)}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2 w-full sm:w-auto"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {rangeButtons.map((b) => {
              const Icon = b.icon;
              return (
                <Button
                  key={b.value}
                  size="sm"
                  variant={range === b.value ? "default" : "outline"}
                  onClick={() => setRange(b.value)}
                  className="gap-1.5 text-xs sm:text-sm flex-1 sm:flex-none"
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{b.label}</span>
                  <span className="sm:hidden">
                    {b.label
                      .replace("Last ", "")
                      .replace(" days", "d")
                      .replace(" month", "m")
                      .replace(" months", "m")
                      .replace(" year", "y")
                      .replace("All time", "All")}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error {error.status}</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>{error.message}</span>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={handleRetry}>
                  Retry
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Chart Section */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-4 text-lg">Registration Trend</h3>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-64 w-full rounded" />
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-4 w-1/2 rounded" />
            </div>
          ) : daily.length === 0 ? (
            <div className="text-center py-12 border rounded bg-muted/30">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                No registrations in this period.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Try selecting a different time range
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={daily}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => format(parseISO(v), "MMM d")}
                  stroke="#6b7280"
                  fontSize={12}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  label={{
                    value: "Registrations",
                    angle: -90,
                    position: "insideLeft",
                    offset: -10,
                    style: { textAnchor: "middle" },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  labelFormatter={(v) =>
                    format(parseISO(v), "EEEE, MMMM d, yyyy")
                  }
                  formatter={(value) => [`${value} users`, "Registrations"]}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Daily Registrations"
                  stroke="#8884d8"
                  strokeWidth={3}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  isAnimationActive={!isRefreshing}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {[
              {
                label: "Total Users",
                value: summary.total,
                icon: Users,
                color: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
              },
              {
                label: "Last 7 Days",
                value: summary.week,
                icon: TrendingUp,
                color: "bg-green-500/10 text-green-700 dark:text-green-300",
              },
              {
                label: "Last Month",
                value: summary.month,
                icon: Calendar,
                color: "bg-accent/10 text-primary dark:text-accent",
              },
              {
                label: "Last 3 Months",
                value: summary.threeMonths,
                icon: Users,
                color: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
              },
              {
                label: "Last Year",
                value: summary.year,
                icon: Shield,
                color: "bg-primary/10 text-primary dark:text-primary/80",
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className={`p-3 sm:p-4 rounded-lg border ${s.color} transition-transform hover:scale-[1.02]`}
                >
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 opacity-70" />
                    <Badge
                      variant="secondary"
                      className="text-xs hidden sm:inline-flex"
                    >
                      {s.label.includes("7")
                        ? "Weekly"
                        : s.label.includes("Month")
                          ? "Monthly"
                          : s.label.includes("Year")
                            ? "Yearly"
                            : "Total"}
                    </Badge>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold">
                    {s.value}
                  </div>
                  <div className="text-xs opacity-70 mt-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Users Table */}
        <div className="border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
            <h3 className="font-semibold text-base sm:text-lg">
              Recent Registrations (10 per page)
            </h3>
            <Badge variant="secondary" className="w-fit">
              Page {page} of {totalPages}
            </Badge>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 border rounded bg-muted/30">
              <UserCheck className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No recent users found.</p>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="block md:hidden space-y-3">
                {recent.map((u) => (
                  <Card key={u.id} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-sm break-all">
                          {u.email}
                        </span>
                        <Badge
                          variant={
                            u.role === "admin"
                              ? "destructive"
                              : u.role === "moderator"
                                ? "secondary"
                                : "outline"
                          }
                          className="text-xs shrink-0"
                        >
                          {u.role}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <Badge
                          variant={u.isVerified ? "default" : "outline"}
                          className="text-xs"
                        >
                          {u.isVerified ? "✓ Verified" : "Pending"}
                        </Badge>
                        <span className="text-muted-foreground">
                          {format(new Date(u.createdAt), "MMM dd, yyyy")}
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead className="text-right">Joined Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((u) => (
                      <TableRow key={u.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{u.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              u.role === "admin"
                                ? "destructive"
                                : u.role === "moderator"
                                  ? "secondary"
                                  : "outline"
                            }
                          >
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.isVerified ? "default" : "outline"}>
                            {u.isVerified ? "✓ Verified" : "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {format(new Date(u.createdAt), "MMM dd, yyyy")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                  <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                    Showing {recent.length} user(s) on page {page} of{" "}
                    {totalPages}
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || loading}
                      className="w-full sm:w-auto text-xs sm:text-sm"
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1 overflow-x-auto max-w-full px-2">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              disabled={loading}
                              className="w-8 h-8 sm:w-9 sm:h-9 p-0 text-xs sm:text-sm"
                            >
                              {pageNum}
                            </Button>
                          );
                        }
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages || loading}
                      className="w-full sm:w-auto text-xs sm:text-sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
